import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadService, dataTransformationService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import {
  ChevronLeft,
  Database,
  FileText,
  Zap,
  Loader2,
  ChevronDown,
  Check,
  Search,
  Clock,
  Server
} from 'lucide-react';

function DataTransformationCreate() {
  const [datasets, setDatasets] = useState({}); // Changed to object for grouped data
  const [groupedDatasets, setGroupedDatasets] = useState({}); // Grouped by clean name
  const [loading, setLoading] = useState(true);
  const [selectedDataset, setSelectedDataset] = useState('');
  const [showDatasetDropdown, setShowDatasetDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [transformationName, setTransformationName] = useState('');
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  
  const { notify } = useNotification();
  const navigate = useNavigate();
  const dropdownContentRef = useRef(null);
  const selectedItemRef = useRef(null);

  useEffect(() => {
    fetchDatasets();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatasetDropdown && !event.target.closest('.dataset-dropdown-container')) {
        setShowDatasetDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatasetDropdown]);

  // Scroll to selected item when dropdown opens
  useEffect(() => {
    if (showDatasetDropdown && selectedDataset && dropdownContentRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const selectedElement = dropdownContentRef.current.querySelector(`[data-dataset-id="${selectedDataset}"]`);
        if (selectedElement) {
          const container = dropdownContentRef.current;
          const elementTop = selectedElement.offsetTop;
          const elementHeight = selectedElement.offsetHeight;
          const containerHeight = container.offsetHeight;
          const scrollTop = container.scrollTop;
          
          // Check if element is not fully visible
          if (elementTop < scrollTop || elementTop + elementHeight > scrollTop + containerHeight) {
            // Scroll to center the element
            container.scrollTop = elementTop - containerHeight / 2 + elementHeight / 2;
          }
        }
      }, 50);
    }
  }, [showDatasetDropdown, selectedDataset]);

  const fetchDatasets = async () => {
    try {
      setLoading(true);
      // Use getDatasets to fetch grouped datasets with versions
      const response = await uploadService.getDatasets();
      const datasetsData = response?.datasets || {};
      
      // Filter to only include datasets with ready status
      const filteredDatasets = {};
      const grouped = {};
      
      Object.entries(datasetsData).forEach(([datasetName, versions]) => {
        const readyVersions = versions.filter(v => v.status === 'ready');
        if (readyVersions.length > 0) {
          // Sort versions: latest first, then by created_at descending
          filteredDatasets[datasetName] = readyVersions.sort((a, b) => {
            if (a.is_latest && !b.is_latest) return -1;
            if (!a.is_latest && b.is_latest) return 1;
            return new Date(b.created_at) - new Date(a.created_at);
          });
          
          // Create a clean grouping key
          const cleanName = getCleanDatasetName(datasetName);
          if (!grouped[cleanName]) {
            grouped[cleanName] = [];
          }
          
          // Add all versions to the group
          readyVersions.forEach(version => {
            grouped[cleanName].push({
              ...version,
              originalName: datasetName,
              displayName: formatDatasetName(datasetName),
              groupName: cleanName
            });
          });
        }
      });
      
      // Sort groups by creation time (newest first) based on the latest version in each group
      const sortedGrouped = {};
      Object.keys(grouped)
        .sort((a, b) => {
          // Get the most recent version from each group (already sorted by API)
          const aLatest = grouped[a][0];
          const bLatest = grouped[b][0];
          // Sort by creation date, newest first
          return new Date(bLatest.created_at) - new Date(aLatest.created_at);
        })
        .forEach(key => {
          // The API already sorted versions with sortVersionsByLatestAndDate
          // Just maintain that order
          sortedGrouped[key] = grouped[key];
        });
      
      setDatasets(filteredDatasets);
      setGroupedDatasets(sortedGrouped);
    } catch (error) {
      console.error('Error fetching datasets:', error);
      notify.error('Failed to load datasets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDataset || !transformationName.trim() || !query.trim()) {
      notify.error('Please fill in all required fields');
      return;
    }

    setCreating(true);

    try {
      const result = await dataTransformationService.createJob({
        dataset_ids: [selectedDataset],
        transformation_name: transformationName.trim(),
        query: query.trim()
      });
      
      notify.success('Transformation job created successfully!');
      // Navigate to the job details page
      navigate(`/data-transformation/${result.job_id}`);
      
    } catch (error) {
      console.error('Error creating transformation:', error);
      notify.error('Failed to create transformation');
    } finally {
      setCreating(false);
    }
  };

  const getCleanDatasetName = (datasetName) => {
    // Extract clean name for grouping
    // e.g., "postgres_6294d4b0_posts" -> "posts"
    // e.g., "customer-analytics" -> "customer-analytics"
    
    const connectorMatch = datasetName.match(/^(postgres|mongo|snowflake)_[a-f0-9]+_(.+)$/);
    if (connectorMatch) {
      return connectorMatch[2];
    }
    return datasetName;
  };

  const formatDatasetName = (datasetName) => {
    // Format dataset names to be more readable
    // e.g., "postgres_6294d4b0_posts" -> "Postgres Posts"
    // e.g., "customer-analytics" -> "Customer Analytics"
    
    // Handle connector-based names (postgres, mongo, snowflake)
    const connectorMatch = datasetName.match(/^(postgres|mongo|snowflake)_[a-f0-9]+_(.+)$/);
    if (connectorMatch) {
      const [, connector, tableName] = connectorMatch;
      return `${connector.charAt(0).toUpperCase() + connector.slice(1)} - ${tableName.replace(/_/g, ' ').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`;
    }
    
    // Format regular dataset names
    return datasetName
      .replace(/-/g, ' ')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const getVersionLabel = (version) => {
    if (version.is_latest) {
      return '✓ Latest';
    } else if (version.version_description && version.version_description !== "Data from postgres connector" && 
               version.version_description !== "Data from mongo connector" && 
               version.version_description !== "Data from snowflake_db connector") {
      return version.version_description;
    } else {
      return new Date(version.created_at).toLocaleDateString();
    }
  };

  const getVersionDetails = (version) => {
    const parts = [];
    if (version.type && version.type !== 'unknown') {
      const typeLabels = { 
        'tabular': 'Table', 
        'images': 'Images', 
        'text_corpus': 'Text', 
        'time_series': 'Time Series' 
      };
      parts.push(typeLabels[version.type] || version.type);
    }
    parts.push(new Date(version.created_at).toLocaleDateString());
    return parts.join(' · ');
  };

  const getSelectedDatasetInfo = () => {
    if (!selectedDataset) return null;
    
    // Find the selected dataset in grouped data
    for (const [groupName, versions] of Object.entries(groupedDatasets)) {
      const found = versions.find(v => v.upload_id === selectedDataset);
      if (found) return found;
    }
    return null;
  };

  const isSelectedLatestInGroup = () => {
    if (!selectedDataset) return false;
    
    const selectedInfo = getSelectedDatasetInfo();
    if (!selectedInfo) return false;
    
    // Find all versions with the same display name
    for (const [groupName, versions] of Object.entries(groupedDatasets)) {
      const sameNameVersions = versions.filter(v => v.displayName === selectedInfo.displayName);
      if (sameNameVersions.length > 0) {
        // Sort by date and check if selected is the latest
        const sorted = sameNameVersions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        return sorted[0].upload_id === selectedDataset;
      }
    }
    return false;
  };

  const filteredGroups = () => {
    if (!searchTerm) return groupedDatasets;
    
    const filtered = {};
    const search = searchTerm.toLowerCase();
    
    Object.entries(groupedDatasets).forEach(([groupName, versions]) => {
      const matchingVersions = versions.filter(v => 
        v.displayName.toLowerCase().includes(search) ||
        groupName.toLowerCase().includes(search) ||
        (v.version_description && v.version_description.toLowerCase().includes(search))
      );
      
      if (matchingVersions.length > 0) {
        filtered[groupName] = matchingVersions;
      }
    });
    
    return filtered;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/data-transformation')}
          className="flex items-center text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 mb-4 transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Transformations
        </button>
        
        <h1 className="text-3xl font-semibold text-slate-900 dark:text-white">Create Transformation</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Transform your dataset using natural language instructions
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6 space-y-6">
          {/* Dataset Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <Database className="inline-block w-4 h-4 mr-1 -mt-0.5" />
              Select Dataset <span className="text-red-500">*</span>
            </label>
            <div className="relative dataset-dropdown-container">
              {/* Custom Dropdown Button */}
              <button
                type="button"
                onClick={() => setShowDatasetDropdown(!showDatasetDropdown)}
                disabled={loading || creating}
                className="w-full px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-left flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="flex items-center gap-2">
                  {loading ? (
                    <span className="text-slate-500 dark:text-slate-400">Loading datasets...</span>
                  ) : selectedDataset ? (
                    <>
                      {getSelectedDatasetInfo()?.displayName && (
                        <>
                          <Server className="h-4 w-4 text-slate-500" />
                          <span>{getSelectedDatasetInfo()?.displayName}</span>
                          {isSelectedLatestInGroup() && (
                            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded">Latest</span>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <span className="text-slate-500 dark:text-slate-400">Select a dataset...</span>
                  )}
                </span>
                <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${showDatasetDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Custom Dropdown Menu */}
              {showDatasetDropdown && !loading && (
                <div className="absolute z-10 mt-2 w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 max-h-96 overflow-hidden">
                  {/* Search Box */}
                  <div className="p-3 border-b border-slate-200 dark:border-slate-700">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search datasets..."
                        className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>

                  {/* Dataset List */}
                  <div ref={dropdownContentRef} className="max-h-72 overflow-y-auto">
                    {Object.entries(filteredGroups()).length === 0 ? (
                      <div className="p-4 text-center text-slate-500 dark:text-slate-400">
                        {searchTerm ? 'No datasets found matching your search' : 'No datasets available'}
                      </div>
                    ) : (
                      Object.entries(filteredGroups()).map(([groupName, versions]) => {
                        // Group similar datasets
                        const uniqueDatasets = {};
                        versions.forEach(version => {
                          const key = version.displayName;
                          if (!uniqueDatasets[key]) {
                            uniqueDatasets[key] = [];
                          }
                          uniqueDatasets[key].push(version);
                        });

                        return Object.entries(uniqueDatasets).map(([displayName, datasetVersions]) => (
                          <div key={displayName} className="border-b border-slate-100 dark:border-slate-700 last:border-0">
                            {datasetVersions.length === 1 ? (
                              // Single version - simple item
                              <button
                                type="button"
                                data-dataset-id={datasetVersions[0].upload_id}
                                onClick={() => {
                                  setSelectedDataset(datasetVersions[0].upload_id);
                                  setShowDatasetDropdown(false);
                                  setSearchTerm('');
                                }}
                                className={`w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${
                                  selectedDataset === datasetVersions[0].upload_id ? 'bg-primary-100 dark:bg-primary-900/40 border-l-4 border-primary-500' : ''
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <Database className="h-4 w-4 text-slate-400" />
                                  <div className="text-left">
                                    <div className="font-medium text-slate-900 dark:text-white">{displayName}</div>
                                    <div className="text-xs text-slate-500 dark:text-slate-400">
                                      {getVersionDetails(datasetVersions[0])}
                                    </div>
                                  </div>
                                </div>
                                {selectedDataset === datasetVersions[0].upload_id && (
                                  <Check className="h-4 w-4 text-primary-500" />
                                )}
                              </button>
                            ) : (
                              // Multiple versions - expandable
                              <div>
                                <div className="px-4 py-2 bg-slate-50 dark:bg-slate-700/50">
                                  <div className="flex items-center gap-2">
                                    <Database className="h-4 w-4 text-slate-400" />
                                    <span className="font-medium text-slate-700 dark:text-slate-300">{displayName}</span>
                                    <span className="text-xs text-slate-500 dark:text-slate-400">({datasetVersions.length} versions)</span>
                                  </div>
                                </div>
                                {datasetVersions
                                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                  .map((version, idx) => {
                                    // Only show "Latest" for the most recent version (first after sorting)
                                    const isActuallyLatest = idx === 0;
                                    return (
                                      <button
                                        key={version.upload_id}
                                        type="button"
                                        data-dataset-id={version.upload_id}
                                        onClick={() => {
                                          setSelectedDataset(version.upload_id);
                                          setShowDatasetDropdown(false);
                                          setSearchTerm('');
                                        }}
                                        className={`w-full pl-12 pr-4 py-2 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${
                                          selectedDataset === version.upload_id ? 'bg-primary-100 dark:bg-primary-900/40 border-l-4 border-primary-500 pl-11' : ''
                                        }`}
                                      >
                                        <div className="flex items-center gap-3">
                                          <Clock className="h-3 w-3 text-slate-400" />
                                          <div className="text-left">
                                            <div className="text-sm text-slate-700 dark:text-slate-300">
                                              {isActuallyLatest ? (
                                                <span className="inline-flex items-center gap-1">
                                                  <span className="text-green-600 dark:text-green-400">✓ Latest version</span>
                                                </span>
                                              ) : (
                                                <span>Version {datasetVersions.length - idx}</span>
                                              )}
                                            </div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400">
                                              {getVersionDetails(version)}
                                            </div>
                                          </div>
                                        </div>
                                        {selectedDataset === version.upload_id && (
                                          <Check className="h-4 w-4 text-primary-500" />
                                        )}
                                      </button>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                        ));
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
            {Object.keys(datasets).length === 0 && !loading && (
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                No datasets available. Please upload a dataset first.
              </p>
            )}
          </div>

          {/* Transformation Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <FileText className="inline-block w-4 h-4 mr-1 -mt-0.5" />
              Transformation Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={transformationName}
              onChange={(e) => setTransformationName(e.target.value)}
              disabled={creating}
              required
              placeholder="e.g., Monthly Revenue Analysis"
              className="w-full px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
            />
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Give your transformation a descriptive name
            </p>
          </div>

          {/* Query */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <Zap className="inline-block w-4 h-4 mr-1 -mt-0.5" />
              Transformation Query <span className="text-red-500">*</span>
            </label>
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={creating}
              required
              placeholder="Describe your transformation in natural language...&#10;&#10;Example: Filter rows where revenue > 1000 and group by truck_id, calculating total revenue and average cost per truck"
              rows={6}
              className="w-full px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 resize-none"
            />
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Describe what you want to do with your data. Our AI will generate optimized Spark code.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/data-transformation')}
            className="px-6 py-3 text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-600 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={creating || !selectedDataset || !transformationName.trim() || !query.trim()}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 font-medium transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none inline-flex items-center"
          >
            {creating ? (
              <>
                <Loader2 className="animate-spin mr-2 h-4 w-4" />
                Creating...
              </>
            ) : (
              'Create Transformation'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DataTransformationCreate;
